// PublicShell.js — hält AuthPage + LandingPage und animiert Logo/Wortmarke
// als "shared elements" fließend zwischen beiden Views.
//
// Kein 3D-Flip — nur elegante Skalierung + Positions-Interpolation
// (CSS transitions, cubic-bezier 650ms).
//
// Die ORIGINALEN Logo-/Wortmarke-Elemente in AuthPage und LandingPage
// werden per `data-shared-brand-placeholder` als versteckte Platzhalter
// belassen — nur dieses geteilte Brand-Element ist visuell sichtbar.
import { useCallback, useEffect, useRef, useState } from 'react';
import AuthPage from './AuthPage';
import LandingPage from './LandingPage';
import { useTheme } from '../contexts/ThemeContext';

const INFO_PATHS = new Set(['/info', '/mehr-infos', '/start']);
const TRANSITION_MS = 650;
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const deriveView = () => {
  const p = typeof window !== 'undefined' ? window.location.pathname : '/';
  return INFO_PATHS.has(p) ? 'landing' : 'auth';
};

// —————————————————————————————————————————————
// Shared Logo (IO) + Wortmarke (Interpreter's Office)
// —————————————————————————————————————————————
const SharedBrand = ({ view }) => {
  const isLanding = view === 'landing';
  const { theme } = useTheme();
  const isLight = theme === 'light';

  // Positionen sind so gewählt, dass sie exakt mit den (versteckten)
  // Placeholders in AuthPage und im LandingPage-Header übereinstimmen.
  // Auf der Anmeldeseite ist die Wortmarke bewusst NICHT fixed — sie soll
  // beim Scrollen ganz normal mit dem restlichen Inhalt mitwandern.
  const logoStyle = {
    position: isLanding ? 'fixed' : 'absolute',
    transitionProperty: 'top, left, width, height, transform, box-shadow',
    transitionDuration: `${TRANSITION_MS}ms`,
    transitionTimingFunction: EASE,
    top: isLanding ? 'calc(14px + env(safe-area-inset-top, 0px))' : '128px',
    left: isLanding
      ? 'calc(50% - min(50%, 640px) + 24px)'
      : '50%',
    transform: isLanding ? 'translateX(0)' : 'translateX(-50%)',
    width: isLanding ? '36px' : '112px',
    height: isLanding ? '36px' : '112px',
    backgroundColor: '#facc15',
    borderRadius: '9999px',
    boxShadow: isLanding
      ? '0 4px 24px rgba(250, 204, 21, 0.4)'
      : '0 8px 40px rgba(250, 204, 21, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 60,
    pointerEvents: 'none',
    opacity: 1,
  };

  const ioTextStyle = {
    transitionProperty: 'font-size, letter-spacing',
    transitionDuration: `${TRANSITION_MS}ms`,
    transitionTimingFunction: EASE,
    fontSize: isLanding ? '18px' : '56px',
    fontWeight: 700,
    color: '#1a1a1a',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    lineHeight: 1,
    letterSpacing: '-0.02em',
    display: 'inline-block',
  };

  const wordmarkStyle = {
    position: isLanding ? 'fixed' : 'absolute',
    transitionProperty:
      'top, left, transform, font-size, color, font-weight, opacity',
    transitionDuration: `${TRANSITION_MS}ms`,
    transitionTimingFunction: EASE,
    top: isLanding ? '186px' : '252px',
    left: '50%',
    transform: isLanding
      ? 'translateX(-50%) scale(0.55)'
      : 'translateX(-50%) scale(1)',
    fontSize: '30px',
    fontWeight: 700,
    color: isLight ? '#000000' : '#facc15',
    opacity: isLanding ? 0 : 1,
    zIndex: 60,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  };

  return (
    <>
      <div style={logoStyle} aria-hidden="true">
        <span style={ioTextStyle}>IO</span>
      </div>
      <div
        style={wordmarkStyle}
        aria-hidden="true"
        data-testid="shared-wordmark"
      >
        Interpreter&apos;s Office
      </div>
    </>
  );
};

const PublicShell = () => {
  const [view, setView] = useState(deriveView);
  const [authRequest, setAuthRequest] = useState(null);
  const [animating, setAnimating] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const onPop = () => setView(deriveView());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const navigate = useCallback(
    (next, mode = 'login') => {
      if (animating || next === view) return;
      setAnimating(true);
      // Scroll SOFORT zurücksetzen — sonst sitzt das SharedLogo bei Rückkehr
      // zur AuthPage an der falschen Y-Position (weil LandingPage evtl.
      // heruntergescrollt war).
      window.scrollTo(0, 0);
      setView(next);
      if (next === 'auth') setAuthRequest({ mode });
      try {
        const path = next === 'landing' ? '/mehr-infos' : mode === 'register' ? '/login?mode=register' : '/login';
        window.history.pushState({}, '', path);
      } catch (e) {
        // ignore
      }
      timeoutRef.current = setTimeout(() => {
        setAnimating(false);
      }, TRANSITION_MS + 30);
    },
    [animating, view]
  );

  const goToLanding = useCallback(() => navigate('landing'), [navigate]);
  const goToAuth = useCallback((mode = 'login') => navigate('auth', mode), [navigate]);

  return (
    <div
      className="relative w-full min-h-screen"
      style={{
        overflowX: 'clip',
      }}
      data-testid="public-shell"
      data-view={view}
    >
      {/* AuthPage-Layer */}
      <div
        aria-hidden={view !== 'auth'}
        inert={view !== 'auth' ? true : undefined}
        style={{
          // 'none' statt 'translateX(0%)' im Ruhezustand — ein transform-Wert
          // (selbst 0%) erzeugt sonst einen neuen containing block für
          // position:fixed-Kinder (Header/Logo/ThemeOrb), die dann relativ
          // zum Dokument statt zum Viewport fixiert wären und beim Scrollen
          // aus dem Sichtbereich verschwinden würden (IO-Logo nicht mehr
          // klickbar, sobald man runterscrollt).
          transform: view === 'auth' ? 'none' : 'translateX(-100%)',
          transition: `transform ${TRANSITION_MS}ms ${EASE}`,
          // Inaktive Ebene MUSS 'fixed' sein (nicht 'absolute') — sonst trägt
          // ihre volle (unsichtbare) Inhaltshöhe zur Scrollhöhe der jeweils
          // AKTIVEN Seite bei ("Boden ohne Hintergrund" beim Runterscrollen).
          position: view === 'auth' ? 'relative' : 'fixed',
          top: 0,
          left: 0,
          right: 0,
          pointerEvents: view === 'auth' ? 'auto' : 'none',
          minHeight: '100vh',
        }}
      >
        <AuthPage onOpenLanding={goToLanding} authRequest={authRequest} />
      </div>

      {/* LandingPage-Layer */}
      <div
        aria-hidden={view !== 'landing'}
        inert={view !== 'landing' ? true : undefined}
        style={{
          transform: view === 'landing' ? 'none' : 'translateX(100%)',
          transition: `transform ${TRANSITION_MS}ms ${EASE}`,
          position: view === 'landing' ? 'relative' : 'fixed',
          top: 0,
          left: 0,
          right: 0,
          pointerEvents: view === 'landing' ? 'auto' : 'none',
          minHeight: '100vh',
        }}
      >
        <LandingPage onGoBackToAuth={goToAuth} />
      </div>

      {/* Shared Logo + Wortmarke (bewegt sich fließend zwischen Views) */}
      <SharedBrand view={view} />
    </div>
  );
};

export default PublicShell;
